import classnames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import useMerchant from 'src/hooks/useMerchant';
import { sendSmsVerifyCode, signIn } from 'src/network/user';
import { storage, validate } from 'src/utils';
import { calcScrollTop } from 'src/utils/tool';
import useInterval from 'src/utils/useInterval';
import { Button, Checkbox, Icon, Input, Popup } from 'zarm';
import InputNumber from '../inputNumber';
import StaticToast from '../toast';
import './index.scss';

interface Props {
  visible: boolean;
  toggleVisible: (status: boolean) => void;
  loginSuccessCallBack: () => void;
  mountContainer: string;
  sendCodeCls?: string;
  loginCls?: string;
}
export default function LoginPopup(props: Props) {
  const { visible, toggleVisible, loginSuccessCallBack, mountContainer, sendCodeCls = '', loginCls = '' } = props;
  const currentMerchant = useMerchant({ stopSetTitle: true });
  const [mobilePhone, setMobilePhone]: [any, any] = useState(storage.get('mobilePhone'));
  const [verifyCode, setVerifyCode]: [any, any] = useState(storage.get('verifyCode'));
  const [count, setCount] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [checked, setChecked]: any = useState(false);
  const [able, setAble] = useState(false);
  const isRestPageScroll = useRef(true);
  const smoothScrollClock: any = useRef<number>();
  const dispatch = useDispatch();

  const handleInput = (value, name) => {
    switch (name) {
      case 'mobilePhone':
        setMobilePhone(value);
        break;

      default:
        setVerifyCode(value?.toString().slice(0, 6));
        break;
    }
    storage.set(name, value);
  };

  const getSendVerifyCode = useCallback(() => {
    if (!mobilePhone) {
      StaticToast.error('手机号码不能为空');
      return;
    }
    if (!validate.isMobile(mobilePhone)) {
      StaticToast.success('您输入的手机号码格式有误');
    }

    sendSmsVerifyCode({
      phone: mobilePhone,
    }).then((res) => {
      if (res && res.success) {
        StaticToast.success('短信发送成功');
        setCount(60);
        setIsRunning(true);
      }
    });
  }, [mobilePhone]);

  const blur = useCallback(() => {
    if (smoothScrollClock.current) {
      clearTimeout(smoothScrollClock.current);
    }
    smoothScrollClock.current = setTimeout(() => {
      // 聚焦时键盘弹出，焦点在输入框之间切换时，会先触发上一个输入框的失焦事件，再触发下一个输入框的聚焦事件
      if (isRestPageScroll.current) {
        window.scroll({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 200);
  }, []);

  const startLogin = useCallback(() => {
    if (mobilePhone === '') {
      StaticToast.warning('请输入手机号码');
      return;
    }

    if (verifyCode === '') {
      StaticToast.warning('请输入短信验证码');
      return;
    }

    signIn({
      phone: mobilePhone,
      smsVerifyCode: verifyCode,
    }).then((res) => {
      if (res.success) {
        const { accountId, token } = res.result;
        if (!accountId || !token) {
          StaticToast.error('登录异常！');
          return;
        }
        storage.set('token', token);
        // dispatch({ type: UserActionTypes.SET_JWT, payload: token });
        storage.set('accountId', accountId);
        storage.remove('mobilePhone');
        storage.remove('verifyCode');
        window._XFLOW_ && window._XFLOW_.setLogin(accountId);
        window?.__SERAPH_HAS_MONITOR__?.setUser?.({ userId: accountId });
        toggleVisible(false);
        loginSuccessCallBack();
      }
    });
  }, [mobilePhone, verifyCode, toggleVisible, loginSuccessCallBack]);

  const handleCheckBox = (e) => {
    setChecked(e.target.checked);
  };

  const resetFocusInputScroll = useCallback((clsName) => {
    if (!validate.isIos()) return;
    setTimeout(() => {
      const currentInput = document.querySelector(`.${clsName}`);
      calcScrollTop(currentInput, window.visualViewport?.height);
    }, 300);
  }, []);

  useEffect(() => {
    if (checked && verifyCode && mobilePhone) {
      setAble(true);
    } else {
      setAble(false);
    }
  }, [checked, verifyCode, mobilePhone]);

  useInterval(
    () => {
      setCount(count - 1);
    },
    isRunning ? 1000 : null,
  );

  useEffect(() => {
    count === 0 && setIsRunning(false);
  }, [count]);

  return (
    <Popup
      visible={visible}
      direction="bottom"
      onMaskClick={() => toggleVisible(false)}
      destroy={false}
      mountContainer={() => document.querySelector(mountContainer) || document.body}
    >
      <div className="popup-box">
        <div className="login-popup">
          <Icon
            className="closeIcon"
            onClick={() => {
              toggleVisible(false);
            }}
            type="wrong"
          />
          <p className="title">{currentMerchant.name}</p>
          <div className="telephone_wrapper">
            <InputNumber
              type="tel"
              className="opt_input opt_input-tel"
              placeholder="请输入11位手机号码"
              maxLength={11}
              value={mobilePhone || storage.get('mobilePhone')}
              onChange={(value) => handleInput(value, 'mobilePhone')}
              onBlur={() => {
                isRestPageScroll.current = true;
                blur();
              }}
              onFocus={() => {
                isRestPageScroll.current = false;
                resetFocusInputScroll('opt_input-tel');
              }}
            />
            {count === 0
              ? (
                <div className={`code ${sendCodeCls}`} onClick={() => getSendVerifyCode()}>
                  发送验证码
                </div>
              )
              : (
                <div className="code">{count}s</div>
              )}
          </div>
          <InputNumber
            type="number"
            className="opt_input opt_input-code"
            placeholder="请输入手机验证码"
            value={verifyCode || storage.get('verifyCode')}
            onChange={(value) => handleInput(value, 'verifyCode')}
            onBlur={() => {
              isRestPageScroll.current = true;
              blur();
            }}
            onFocus={() => {
              isRestPageScroll.current = false;
              resetFocusInputScroll('opt_input-code');
            }}
          />
          <div className="tips">
            <p>
              <Checkbox
                id="agreement"
                checked={checked}
                onChange={(e) => {
                  handleCheckBox(e);
                }}
              />
            </p>
            <p>
              若手机号未注册，将会进入注册流程。注册即视为同意
              <span>
                <Link to={{ pathname: '/static/agreement' }}>
                  《{currentMerchant.serviceDocName || '众安用户服务协议'}》
                </Link>
                、
                <Link to={{ pathname: '/static/private' }}>
                  《{currentMerchant.privateDocName || '众安个人信息保护政策'}》
                </Link>
              </span>
            </p>
          </div>
          <Input style={{ display: 'none' }} />
          <Button
            theme="primary"
            disabled={!able}
            className={`${classnames('unable', { login: able })} ${loginCls}`}
            onClick={() => startLogin()}
          >
            登录
          </Button>
        </div>
      </div>
    </Popup>
  );
}
